import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
    ArrowLeft, BarChart3, BookOpen, Calculator, CalendarDays, ChevronRight,
    CircleQuestionMark, CreditCard, FileText, Landmark, LifeBuoy, ListChecks, Loader2,
    Rocket,
    PiggyBank, Receipt, RefreshCw, Search, Settings, Smartphone, Sparkles,
    Stethoscope, TriangleAlert, Undo2, Users, Wallet, Wrench, type LucideIcon,
} from 'lucide-react';
import { useHelpArticles } from '../hooks/useHelpArticles';
import { HELP_CATEGORIES, searchArticles, type HelpArticle } from '../lib/help';
import { MarkdownArticle } from '../components/help/MarkdownArticle';
import { DiagnosticsPanel } from '../components/help/DiagnosticsPanel';
import { FlowDiagram } from '../components/help/FlowDiagram';

const ICONS: Record<string, LucideIcon> = {
    BarChart3, BookOpen, Calculator, CalendarDays, CircleQuestionMark, FileText,
    CreditCard, Landmark, LifeBuoy, ListChecks, PiggyBank, Receipt, RefreshCw, Rocket, Settings,
    Smartphone, Sparkles, Stethoscope, TriangleAlert, Undo2, Users, Wallet, Wrench,
};

/**
 * Renders an article's icon by name. A plain function rather than a component,
 * so the icon lookup isn't mistaken for a component defined during render.
 */
const renderIcon = (name: string, className: string) => {
    const Glyph: LucideIcon = ICONS[name] ?? FileText;
    return <Glyph className={className} strokeWidth={2} />;
};

/** Articles that carry an interactive panel below the prose. */
const DIAGNOSTICS_SLUG = 'troubleshooting';

/**
 * Graphics an article can request via its `visual` field. Keyed by name rather
 * than by slug so a database-authored article can opt into one too — the point
 * of DB-backed content is not having to deploy to use what's already built.
 */
const VISUALS: Record<string, () => React.ReactElement> = {
    flow: () => <FlowDiagram />,
};

const renderVisual = (name?: string) => {
    const build = name ? VISUALS[name] : undefined;
    return build ? build() : null;
};

// ── Index ───────────────────────────────────────────────────────────────────

const ArticleRow = ({ article }: { article: HelpArticle }) => {
    return (
        <Link
            to={`/help/${article.slug}`}
            className="flex items-start gap-3.5 p-4 rounded-xl bg-panel border border-line hover:border-gold/40 hover:bg-card transition-colors group"
        >
            <span className="shrink-0 w-10 h-10 rounded-lg grid place-items-center bg-gold-fill/10 border border-gold/20">
                {renderIcon(article.icon, 'w-5 h-5 text-gold')}
            </span>
            <span className="flex-1 min-w-0">
                <span className="block text-[16px] font-semibold text-fg leading-snug">
                    {article.title}
                </span>
                {article.summary && (
                    <span className="block text-[14px] text-fg-3 leading-snug mt-1">
                        {article.summary}
                    </span>
                )}
            </span>
            <ChevronRight className="shrink-0 w-5 h-5 mt-2 text-fg-4 group-hover:text-gold transition-colors" />
        </Link>
    );
};

const HelpIndex = ({ articles }: { articles: HelpArticle[] }) => {
    const [query, setQuery] = useState('');
    const results = useMemo(() => searchArticles(articles, query), [articles, query]);
    const searching = query.trim().length > 0;

    const byCategory = useMemo(() => {
        return HELP_CATEGORIES
            .map((category) => ({
                category,
                items: articles.filter((a) => a.category === category),
            }))
            .filter((group) => group.items.length > 0);
    }, [articles]);

    return (
        <div className="max-w-3xl">
            <header className="mb-6">
                <h1 className="text-2xl lg:text-3xl font-bold font-display text-fg">
                    Help &amp; Guides
                </h1>
                <p className="text-[16px] text-fg-3 mt-1">
                    How the app works, and how to get the most out of it.
                </p>
            </header>

            <div className="relative mb-6">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-fg-4 pointer-events-none" />
                <input
                    type="search"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search the guides…"
                    aria-label="Search the guides"
                    className="w-full h-12 pl-11 pr-4 rounded-xl bg-panel border border-line text-fg placeholder-fg-4 outline-none focus:border-gold focus:shadow-focus transition-all"
                />
            </div>

            {searching ? (
                results.length > 0 ? (
                    <div className="space-y-2.5">
                        {results.map((a) => <ArticleRow key={a.slug} article={a} />)}
                    </div>
                ) : (
                    <div className="text-center py-14 px-6 rounded-xl border border-dashed border-line">
                        <BookOpen className="w-8 h-8 mx-auto text-fg-4 mb-3" />
                        <p className="text-fg-2 font-medium">Nothing matched “{query}”.</p>
                        <p className="text-sm text-fg-3 mt-1">
                            Try a shorter word, or browse the sections below.
                        </p>
                        <button
                            onClick={() => setQuery('')}
                            className="mt-4 text-sm font-semibold text-gold hover:underline"
                        >
                            Clear search
                        </button>
                    </div>
                )
            ) : (
                <div className="space-y-8">
                    {byCategory.map(({ category, items }) => (
                        <section key={category}>
                            <h2 className="text-[12px] uppercase tracking-[0.16em] font-semibold font-display text-fg-4 mb-3">
                                {category}
                            </h2>
                            <div className="space-y-2.5">
                                {items.map((a) => <ArticleRow key={a.slug} article={a} />)}
                            </div>
                        </section>
                    ))}
                </div>
            )}
        </div>
    );
};

// ── Article ─────────────────────────────────────────────────────────────────

const HelpArticleView = ({ article, all }: { article: HelpArticle; all: HelpArticle[] }) => {
    // "Next" walks the reader on through the same section rather than dropping
    // them back at the index every time.
    const siblings = all.filter((a) => a.category === article.category);
    const next = siblings[siblings.findIndex((a) => a.slug === article.slug) + 1];

    return (
        <div className="max-w-2xl">
            <Link
                to="/help"
                className="inline-flex items-center gap-2 text-sm text-fg-3 hover:text-fg transition-colors mb-6"
            >
                <ArrowLeft className="w-4 h-4" />
                All guides
            </Link>

            <header className="flex items-start gap-3.5 mb-7">
                <span className="shrink-0 w-11 h-11 rounded-xl grid place-items-center bg-gold-fill/10 border border-gold/20">
                    {renderIcon(article.icon, 'w-5 h-5 text-gold')}
                </span>
                <div className="min-w-0">
                    <p className="text-[11px] uppercase tracking-[0.16em] font-semibold text-fg-4 mb-1">
                        {article.category}
                    </p>
                    <h1 className="text-2xl lg:text-3xl font-bold font-display text-fg leading-tight">
                        {article.title}
                    </h1>
                </div>
            </header>

            {renderVisual(article.visual)}

            <MarkdownArticle markdown={article.body} />

            {article.slug === DIAGNOSTICS_SLUG && <DiagnosticsPanel />}

            {next && (
                <Link
                    to={`/help/${next.slug}`}
                    className="mt-10 flex items-center justify-between gap-4 p-4 rounded-xl bg-panel border border-line hover:border-gold/40 transition-colors group"
                >
                    <span className="min-w-0">
                        <span className="block text-[11px] uppercase tracking-[0.16em] text-fg-4 mb-0.5">
                            Next
                        </span>
                        <span className="block text-[16px] font-semibold text-fg truncate">
                            {next.title}
                        </span>
                    </span>
                    <ChevronRight className="shrink-0 w-5 h-5 text-fg-4 group-hover:text-gold transition-colors" />
                </Link>
            )}
        </div>
    );
};

// ── Page ────────────────────────────────────────────────────────────────────

export default function HelpCentre() {
    const { slug } = useParams<{ slug?: string }>();
    const { articles, loading } = useHelpArticles();

    // Opening a guide from halfway down the previous one used to leave you
    // halfway down the new one — the router keeps scroll position across
    // same-component navigations.
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'auto' });
    }, [slug]);

    if (loading) {
        return (
            <div className="flex items-center justify-center py-24">
                <Loader2 className="w-6 h-6 animate-spin text-gold" />
            </div>
        );
    }

    if (!slug) return <HelpIndex articles={articles} />;

    const article = articles.find((a) => a.slug === slug);
    if (!article) {
        return (
            <div className="max-w-2xl text-center py-20">
                <CircleQuestionMark className="w-9 h-9 mx-auto text-fg-4 mb-4" />
                <h1 className="text-xl font-bold text-fg">Guide not found</h1>
                <p className="text-fg-3 mt-2">
                    It may have been renamed, or it may not apply to your role.
                </p>
                <Link
                    to="/help"
                    className="inline-flex items-center gap-2 mt-6 h-11 px-5 rounded-lg font-semibold fd-btn-primary transition-all active:scale-[0.98]"
                >
                    Browse all guides
                </Link>
            </div>
        );
    }

    return <HelpArticleView article={article} all={articles} />;
}
